import { Modal, Table, Tag, Alert } from 'antd';

// ════════════════════════════════════════════════════════════════════
// Stage 6PL-1e — "this customer pays differently. Re-price these lines?"
//
// Shown ONLY when the diff is non-empty (decision D3). The three things that
// make it safe to say yes are all visible on it: exactly which lines move,
// exactly what they move from and to, and which agreement is responsible.
//
// What is NOT on it is as important: hand-typed lines never appear, because
// they are excluded upstream in buildRepriceDiff rather than merely unticked
// here. A dialog that could overwrite an operator's own number — even by
// accident, even by a stray Enter — would be worse than no dialog.
// ════════════════════════════════════════════════════════════════════

const money = (v) => Number(v).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const SOURCE_TAG = {
  CUSTOMER_LIST: { color: 'blue', label: 'customer list' },
  GROUP_LIST: { color: 'geekblue', label: 'group list' },
  MASTER: { color: 'default', label: 'item master' }
};

export default function RepriceCustomerModal({ open, diff, customerName, onKeep, onApply }) {
  const rows = Array.isArray(diff) ? diff : [];
  const cheaper = rows.filter((d) => d.to < d.from).length;
  const dearer = rows.length - cheaper;

  const columns = [
    { title: '#', dataIndex: 'lineNo', width: 44, align: 'right' },
    { title: 'Item', dataIndex: 'itemDesc', ellipsis: true },
    {
      title: 'Now',
      dataIndex: 'from',
      width: 96,
      align: 'right',
      render: (v) => <span style={{ fontVariantNumeric: 'tabular-nums', color: '#64748b' }}>₹{money(v)}</span>
    },
    {
      title: 'Becomes',
      dataIndex: 'to',
      width: 104,
      align: 'right',
      render: (v, r) => (
        <span style={{ fontVariantNumeric: 'tabular-nums', fontWeight: 600, color: v < r.from ? '#15803d' : '#b91c1c' }}>
          ₹{money(v)}
        </span>
      )
    },
    {
      title: 'From',
      dataIndex: 'source',
      width: 140,
      render: (v, r) => {
        const t = SOURCE_TAG[v] || { color: 'default', label: v };
        return <Tag color={t.color} style={{ marginInlineEnd: 0 }}>{r.listCode || t.label}</Tag>;
      }
    }
  ];

  return (
    <Modal
      open={open} width={680} destroyOnHidden
      title="This customer pays different rates"
      onCancel={onKeep}
      okText={`Re-price ${rows.length} line${rows.length === 1 ? '' : 's'}`}
      cancelText="Keep current rates"
      onOk={onApply}
    >
      <Alert
        type="info" showIcon style={{ marginBottom: 12, fontSize: 12 }}
        message={customerName
          ? `${customerName} has a price list that quotes some of these items differently.`
          : 'The selected customer has different rates for some of these items.'}
        description="Nothing has changed yet. Lines you typed a rate into by hand are not listed and are never touched."
      />
      <Table
        size="small" bordered rowKey="refUuid" columns={columns} dataSource={rows}
        pagination={false} scroll={{ y: 300 }}
      />
      <div style={{ fontSize: 11, color: '#64748b', marginTop: 8 }}>
        {cheaper} line{cheaper === 1 ? '' : 's'} cheaper · {dearer} dearer.
        Other charges and fabrication recalculate from the new rates when you save.
      </div>
    </Modal>
  );
}
